import React from 'react';
import { Modal, Box, Typography, Button, Select, MenuItem, TextField } from '@mui/material';
import Grid from "@mui/material/Grid2";
import PatientSearchBox from '../patient/PatientSearchBox';
import { format } from 'date-fns';
import { gql, useMutation } from '@apollo/client';
import FilterGridRow from './FilterGridRow';
import { calculateDuration, getServiceDefaults } from '~/utils/visitsHelper';
import { useSnackbar } from '~/hooks/useSnackbar';
import SelectServices from './SelectServices';
import { useOrgConfig, VisitType } from "~/providers/OrgConfigProvider";
import { useSelectableServiceLineIds } from '~/hooks/useDefaultServiceLines';

interface CreateVisitRequestProps {
  open: boolean;
  handleClose: () => void;
  calendarDateTime: Date;
  onSuccess: (visitId: number) => void;
}

const CreateVisitRequest: React.FC<CreateVisitRequestProps> = ({
  open, handleClose, calendarDateTime
}) => {
  const [selectedPatient, setSelectedPatient] = React.useState<{ id: number, title: string } | undefined>();
  const [createVisitRequest, { loading: creatingVisitRequest }] = useMutation(CREATE_VISIT_REQUEST_MUTATION, {
    refetchQueries: ['CommandIndexCombinedData']
  });

  const formattedDate = (calendarDateTime && !isNaN(calendarDateTime.getTime()) ? format(calendarDateTime, 'PP') : '');

  const orgConfig = useOrgConfig();
  const serviceLineIds = useSelectableServiceLineIds();

  const [selectedServiceLineId, setSelectedServiceLineId] = React.useState<number | null>(serviceLineIds[0] ?? null);
  const [selectedVisitType, setSelectedVisitType] = React.useState<VisitType | null>(null);
  const [selectedServicesIds, setSelectedServicesIds] = React.useState<Set<number>>(
    new Set<number>()
  );
  const [duration, setDuration] = React.useState<number | undefined>();

  const currentServiceLine = selectedServiceLineId ? orgConfig.serviceLines[selectedServiceLineId] : undefined;

  const allowedVisitTypes = currentServiceLine?.visitTypeServiceLines.map(vtsl => vtsl.visitTypeId);

  const filteredVisitTypes = Object.values(orgConfig.visitTypes).filter(vt => vt !== undefined).filter(vt => allowedVisitTypes?.includes(vt.id))

  const calculatedDuration = calculateDuration(
    orgConfig,
    selectedVisitType?.id,
    [...selectedServicesIds.values()].map(String)
  );

  const { setErrorMessage: displayError } = useSnackbar();

  const handleVisitTypeChange = (visitTypeId: number) => {
    const visitType = orgConfig.visitTypes[visitTypeId];
    if (!visitType) return;
    setSelectedVisitType(visitType);
    const serviceDefaults = getServiceDefaults(orgConfig, visitTypeId);
    if (serviceDefaults) {
      setSelectedServicesIds(new Set(serviceDefaults.map(serviceId => parseInt(serviceId, 10))));
    }
  };

  const handleServiceLineChange = (serviceLineId: number) => {
    setSelectedServiceLineId(serviceLineId);
    setSelectedVisitType(null);
    setSelectedServicesIds(new Set<number>());
  };

  const reset = () => {
    setSelectedPatient(undefined);
    setSelectedServiceLineId(null);
    setSelectedVisitType(null);
    setSelectedServicesIds(new Set<number>());
    setDuration(undefined);
  };

  const shouldDisableButton = !selectedPatient?.id || !selectedServiceLineId || !selectedVisitType;

  const handleContinue = async () => {
    try {
      await createVisitRequest({
        variables: {
          patientId: selectedPatient?.id,
          serviceLineId: selectedServiceLineId,
          visitTypeId: selectedVisitType?.id,
          duration: parseInt(`${duration || calculatedDuration}`, 10),
          serviceIds: selectedServicesIds.size ? Array.from(selectedServicesIds) : undefined,
          constraints: {
            preferredTime: format(calendarDateTime, "yyyy-MM-dd'T'HH:mm:ss")
          }
      }});

      handleClose();
      reset();
    } catch (error) {
      console.error('Error generating visit warnings:', error);
      displayError('An unexpected error occurred');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 400,
        bgcolor: 'background.paper',
        p: 4
      }}>
        <Typography id="modal-title" variant="h6" component="h2">
          Create Visit Request for {formattedDate}
        </Typography>
        <Box id="modal-description" sx={{ mt: 2 }}>
          <Typography variant='subtitle2'>Please select a patient</Typography>
          <PatientSearchBox emptyTextInputAfterSelect={false} onPatientClicked={(patient) => {
            reset();
            setSelectedPatient(patient);
          }} />

          <Box sx={{ my: 3 }}>
            {selectedPatient ? (
              <>
                <Grid container direction='row' spacing={2} mb={2}>
                  <Grid size={6}>
                    <FilterGridRow fullWidth label="Service Line" loading={orgConfig.loading}>
                      <Select
                        value={selectedServiceLineId ?? ''}
                        size='small'
                        fullWidth
                        onChange={(e) => handleServiceLineChange(parseInt(`${e.target.value}`, 10))}
                      >
                        {serviceLineIds.map((id) => (
                          <MenuItem key={id} value={id}>
                            {orgConfig.serviceLines[id]?.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FilterGridRow>
                  </Grid>

                  <Grid size={6}>
                    {selectedServiceLineId && <FilterGridRow fullWidth label="Visit Type" loading={orgConfig.loading}>
                      <Select
                        value={selectedVisitType?.id ?? ''}
                        size='small'
                        fullWidth
                        onChange={e => handleVisitTypeChange(parseInt(`${e.target.value}`, 10))}
                      >
                        {filteredVisitTypes?.map((visitType) => (
                          <MenuItem key={visitType.id} value={visitType.id}>
                            {visitType.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FilterGridRow>}
                  </Grid>
                </Grid>

                {selectedVisitType?.visitTypeServices?.length ? (
                  <SelectServices
                    serviceOptions={selectedVisitType?.visitTypeServices}
                    selectedServicesIds={selectedServicesIds}
                    setSelectedServicesIds={setSelectedServicesIds}
                  />
                ) : null}

                {selectedVisitType && (
                  <Grid container direction='row' spacing={2} mb={2}>
                    <Grid size={3}>
                      <FilterGridRow fullWidth label="Duration" loading={orgConfig.loading}>
                        <TextField
                          size='small'
                          value={duration === 0 ? duration : (duration || calculatedDuration)}
                          sx={{ width: '50%'}}
                          onChange={(e) => {
                            if (!e.target.value) {
                              setDuration(0);
                              return;
                            }
                            setDuration(parseInt(e.target.value, 10));
                          }}
                        />
                      </FilterGridRow>
                    </Grid>
                  </Grid>
                )}
              </>
            ) : null}
          </Box>
        </Box>

        <Box justifySelf='flex-end'>
          <Button
            onClick={() => {
              handleClose();
              reset();
            }}
            sx={{ mr: 4 }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            size='large'
            onClick={() => {
              handleContinue();
            }}
            disabled={shouldDisableButton}
            loading={creatingVisitRequest}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateVisitRequest;

const CREATE_VISIT_REQUEST_MUTATION = gql`
  mutation CreateVisitRequest(
    $patientId: Int!,
    $serviceLineId: Int!,
    $visitTypeId: Int!,
    $duration: Int!,
    $serviceInstructions: String,
    $serviceIds: [Int],
    $constraints: Constraints
  )
  @permission(requires: createDragEvents, check: [userInServiceLine])
  {
    createVisitRequest(
      patientId: $patientId,
      serviceLineId: $serviceLineId,
      visitTypeId: $visitTypeId,
      duration: $duration,
      serviceInstructions: $serviceInstructions,
      serviceIds: $serviceIds,
      constraints: $constraints,
    ) {
      id
    }
  }
`;
